import React, { useEffect, Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';

// Code-split all non-critical routes
const AboutPage = lazy(() => import('./pages/AboutPage'));
const DigitalMarketingPage = lazy(() => import('./pages/DigitalMarketingPage'));
const BeatlesLandingPage = lazy(() => import('./pages/BeatlesLandingPage'));
const SeoPage = lazy(() => import('./pages/SeoPage'));
const WebsitesPage = lazy(() => import('./pages/WebsitesPage'));
const BrandingPage = lazy(() => import('./pages/BrandingPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const ContactThankYouPage = lazy(() => import('./pages/ContactThankYouPage'));
const CaseStudyPage = lazy(() => import('./pages/CaseStudyPage'));
const CaseStudiesPage = lazy(() => import('./pages/CaseStudiesPage'));
const BespokeCrmPage = lazy(() => import('./pages/BespokeCrmPage'));
const PackagePage = lazy(() => import('./pages/PackagePage'));
const WebsiteBuilderPage = lazy(() => import('./pages/WebsiteBuilderPage'));
const WebsiteBuilderDepositThankYouPage = lazy(() => import('./pages/WebsiteBuilderDepositThankYouPage'));
const WebsiteBuilderPayPage = lazy(() => import('./pages/WebsiteBuilderPayPage'));
const BookingPage = lazy(() => import('./pages/BookingPage'));
const BookingThankYouPage = lazy(() => import('./pages/BookingThankYouPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const AllArticlesPage = lazy(() => import('./pages/AllArticlesPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const QuizPage = lazy(() => import('./pages/QuizPage'));
const QuizThankYouPage = lazy(() => import('./pages/QuizThankYouPage'));
const QuizResultsPage = lazy(() => import('./pages/QuizResultsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const SectorPage = lazy(() => import('./pages/SectorPage'));
const MarketingHealthCheckPage = lazy(() => import('./pages/MarketingHealthCheckPage'));
const AdGrantPage = lazy(() => import('./pages/AdGrantPage'));
const DemandGenPage = lazy(() => import('./pages/DemandGenPage'));
const PricingGuidePage = lazy(() => import('./pages/PricingGuidePage'));
const CalculatorPage = lazy(() => import('./pages/CalculatorPage'));
const ComparePricesPage = lazy(() => import('./pages/ComparePricesPage'));
const ServiceLandingPage = lazy(() => import('./pages/ServiceLandingPage'));
const BenchmarksPage = lazy(() => import('./pages/BenchmarksPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const WebinarsPage = lazy(() => import('./pages/WebinarsPage'));
const CoursesPage = lazy(() => import('./pages/CoursesPage'));
const CourseDetailPage = lazy(() => import('./pages/CourseDetailPage'));
const MyLearningPage = lazy(() => import('./pages/MyLearningPage'));
const MyTeamPage = lazy(() => import('./pages/MyTeamPage'));
const JoinTeamPage = lazy(() => import('./pages/JoinTeamPage'));
const AdminCoursesPage = lazy(() => import('./pages/AdminCoursesPage'));
const LmsPaymentSuccessPage = lazy(() => import('./pages/LmsPaymentSuccessPage'));
const CertificatePage = lazy(() => import('./pages/CertificatePage'));
const NewsletterThankYouPage = lazy(() => import('./pages/NewsletterThankYouPage'));

import QuizPopup from './components/QuizPopup';
import ChatWidget from './components/ChatWidget';
import { LmsAuthCallback } from './lms/LmsAuthCallback';
import { Toaster } from 'sonner';

const PageLoader = () => (
  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ width: 40, height: 40, border: '3px solid #e2e8f0', borderTopColor: 'var(--teal)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Safeguard: if the edge ever serves the main SPA for a bare "/crm" (no trailing
// slash), bounce to "/crm/" which resolves to the CRM app's build/crm/index.html.
function CrmRedirect() {
  useEffect(() => {
    window.location.replace('/crm/' + (window.location.hash || ''));
  }, []);
  return <PageLoader />;
}

// CRM public links (unsubscribe, preferences, booking, forms, proposals, funnels) live
// inside the hash-routed CRM app at /crm/#/... . Campaign emails historically point them
// at the main-domain root, which 404s here, so forward them (with their token/query) into
// the CRM. This also fixes links in emails already sitting in inboxes.
function PublicCrmRedirect() {
  useEffect(() => {
    const { pathname, search, hash, host, origin } = window.location;
    // If opened on a preview/localhost host (e.g. from an already-sent email), send
    // the recipient to the LIVE domain so unsubscribe/preferences always work (ICO).
    const isEphemeral = /\.preview\.emergentagent\.com$/.test(host) || /^localhost/.test(host) || /^127\.0\.0\.1/.test(host);
    const base = isEphemeral ? 'https://bluecactus.digital' : origin;
    window.location.replace(base + '/crm/#' + pathname + search + hash);
  }, []);
  return <PageLoader />;
}

function AppContent() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <LmsAuthCallback />;
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <QuizPopup />
      <ChatWidget />
      <main>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/beatles" element={<BeatlesLandingPage />} />
            <Route path="/seo" element={<SeoPage />} />
            <Route path="/thorpe-hall-beatles" element={<BeatlesLandingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/digital-marketing" element={<DigitalMarketingPage />} />
            <Route path="/digital-marketing/:slug" element={<CaseStudyPage />} />
            <Route path="/websites" element={<WebsitesPage />} />
            <Route path="/services-and-what-we-do/web-seo/:slug" element={<CaseStudyPage />} />
            <Route path="/branding" element={<BrandingPage />} />
            <Route path="/services-and-what-we-do/branding/:slug" element={<CaseStudyPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/contact/thank-you" element={<ContactThankYouPage />} />
            <Route path="/case-study/:slug" element={<CaseStudyPage />} />
            <Route path="/digital-marketing-case-studies" element={<CaseStudiesPage />} />
            <Route path="/bespoke-crm-systems" element={<BespokeCrmPage />} />
            <Route path="/packages" element={<PackagePage />} />
            <Route path="/packages/:slug" element={<PackagePage />} />
            <Route path="/website-builder" element={<WebsiteBuilderPage />} />
            <Route path="/website-builder/deposit-thank-you" element={<WebsiteBuilderDepositThankYouPage />} />
            <Route path="/website-builder/pay/:submissionId" element={<WebsiteBuilderPayPage />} />
            <Route path="/book-online" element={<BookingPage />} />
            <Route path="/book-online/thank-you" element={<BookingThankYouPage />} />
            <Route path="/sectors" element={<SectorPage />} />
            <Route path="/marketing-health-check" element={<MarketingHealthCheckPage />} />
            <Route path="/google-ad-grant-for-charities" element={<AdGrantPage />} />
            <Route path="/healthtech-demand-generation" element={<DemandGenPage />} />
            <Route path="/charity-marketing-cost" element={<PricingGuidePage variant="charity" />} />
            <Route path="/care-technology-marketing-cost" element={<PricingGuidePage variant="care-tech" />} />
            <Route path="/marketing-roi-calculator" element={<CalculatorPage variant="roi" />} />
            <Route path="/google-ad-grant-calculator" element={<CalculatorPage variant="adgrant" />} />
            <Route path="/marketing-pricing-comparison" element={<ComparePricesPage />} />
            <Route path="/services/:slug" element={<ServiceLandingPage />} />
            <Route path="/marketing-benchmarks-2026" element={<BenchmarksPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/webinars" element={<WebinarsPage />} />
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/academy" element={<Navigate to="/courses" replace />} />
            <Route path="/courses/payment/success" element={<LmsPaymentSuccessPage />} />
            <Route path="/courses/:slug" element={<CourseDetailPage />} />
            <Route path="/certificates/:certId" element={<CertificatePage />} />
            <Route path="/thank-you-subscribing" element={<NewsletterThankYouPage />} />
            <Route path="/my-learning" element={<MyLearningPage />} />
            <Route path="/my-team" element={<MyTeamPage />} />
            <Route path="/courses/join-team/:token" element={<JoinTeamPage />} />
            <Route path="/admin/courses" element={<AdminCoursesPage />} />
            <Route path="/sectors/:slug" element={<SectorPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/all" element={<AllArticlesPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/blog-archive" element={<BlogPage />} />
            <Route path="/:slug" element={<BlogPostPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/quiz/:stepSlug?" element={<QuizPage />} />
            <Route path="/quiz/results" element={<QuizResultsPage />} />
            <Route path="/quiz/results/:sharedId" element={<QuizResultsPage />} />
            <Route path="/quiz/thank-you" element={<QuizThankYouPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/crm" element={<CrmRedirect />} />
            <Route path="/unsubscribe" element={<PublicCrmRedirect />} />
            <Route path="/preferences/:token" element={<PublicCrmRedirect />} />
            <Route path="/m/:id" element={<PublicCrmRedirect />} />
            <Route path="/f/:formId" element={<PublicCrmRedirect />} />
            <Route path="/proposal/:token" element={<PublicCrmRedirect />} />
            <Route path="/p/:slug" element={<PublicCrmRedirect />} />
            <Route path="/p/:slug/:stepSlug" element={<PublicCrmRedirect />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}

export default App;
;
